import { z } from 'zod';
import { IntegrationType } from '../lib/integration';

export const AddProviderStep = {
  INITIAL: 'INITIAL',
  LOCATION: 'LOCATION',
  CONTACT: 'CONTACT',
  AUTHENTICATION: 'AUTHENTICATION',
} as const;

export type AddProviderStep =
  (typeof AddProviderStep)[keyof typeof AddProviderStep];

/** Every field of every step; the steps stay mounted, so all are in the form. */
export type AddProviderValues = {
  integration_type?: string;
  name?: string;
  email?: string;
  address_line_one?: string;
  address_line_two?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  is_fulfillable?: boolean;
  is_warehouse?: boolean;
  username?: string;
  password?: string;
  host?: string;
  depositorCode?: string;
  homeDirectory?: string;
  homeDirectoryShipments?: string;
  fulfillment_services?: boolean;
  quantity_type?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  contact_fax?: string;
  client_id?: string;
  client_secret?: string;
  clientName?: string;
};

/* Integrations whose one credentials step stands in for location and contact. */
const ONE_STEP: string[] = [
  IntegrationType.skudrop,
  IntegrationType.tactical_logistics,
  IntegrationType.mintsoft,
  IntegrationType.santaFe,
  IntegrationType.camelot,
  IntegrationType.shipHero,
  IntegrationType.goodCang,
];

export function isOneStepIntegration(type: string | undefined): boolean {
  return !!type && ONE_STEP.includes(type);
}

/* Only the warehouses the user runs or names themselves take a name. */
const NAMED: string[] = [
  IntegrationType['3pl'],
  IntegrationType.custom3PL,
  IntegrationType.manual,
];

const isEmail = (value: string) => z.string().email().safeParse(value).success;

type Check = {
  field: keyof AddProviderValues;
  /** The first failing rule's message, or nothing. */
  test: (values: AddProviderValues) => string | undefined;
};

const text = (values: AddProviderValues, field: keyof AddProviderValues) => {
  const value = values[field];
  return typeof value === 'string' ? value : '';
};

const required =
  (
    field: keyof AddProviderValues,
    message: string,
    trim = true,
  ): Check['test'] =>
  values => {
    const value = text(values, field);
    return (trim ? value.trim() : value) ? undefined : message;
  };

const requiredEmail =
  (
    field: keyof AddProviderValues,
    requiredMessage: string,
    emailMessage: string,
  ): Check['test'] =>
  values => {
    const value = text(values, field);
    if (!value) return requiredMessage;
    return isEmail(value) ? undefined : emailMessage;
  };

const when =
  (condition: (type: string) => boolean, test: Check['test']): Check['test'] =>
  values =>
    condition(values.integration_type ?? '') ? test(values) : undefined;

const is =
  (...types: string[]) =>
  (type: string) =>
    types.includes(type);

const notOneStep = (type: string) => !isOneStepIntegration(type);

function usernameRule(values: AddProviderValues): string | undefined {
  switch (values.integration_type) {
    case IntegrationType.shipHero:
      return required(
        'username',
        'Refresh Token is required field',
        false,
      )(values);
    case IntegrationType.santaFe:
    case IntegrationType.camelot:
      return required('username', 'Name is required field', false)(values);
    case IntegrationType.mintsoft:
      return required('username', 'User Name is required field', false)(values);
    case IntegrationType.skudrop:
    case IntegrationType.goodCang:
      return required('username', 'Api Key is required field', false)(values);
    case IntegrationType.tactical_logistics:
      return requiredEmail(
        'username',
        'Email Address is required field',
        'Please enter valid e-mail',
      )(values);
    default:
      return undefined;
  }
}

function hostRule(values: AddProviderValues): string | undefined {
  switch (values.integration_type) {
    case IntegrationType.goodCang:
      return required('host', 'App Token is required', false)(values);
    case IntegrationType.santaFe:
    case IntegrationType.camelot:
      return required('host', 'Host is required field', false)(values);
    default:
      return undefined;
  }
}

function phoneRule(dialCodeLength: number): Check['test'] {
  return values => {
    const phone = text(values, 'contact_phone').trim();
    if (!phone) return 'Contact Phone is required';
    if (phone.length < 6 + dialCodeLength) {
      return 'Contact Phone must be atleast 6 digits';
    }
    if (phone.length > 12 + dialCodeLength) {
      return 'Contact Phone cannot be more than 12 digits';
    }
    return undefined;
  };
}

function stepChecks(
  step: AddProviderStep,
  { changeTypeOnly, dialCodeLength }: SchemaOptions,
): Check[] {
  switch (step) {
    case AddProviderStep.INITIAL:
      return [
        {
          field: 'integration_type',
          test: required(
            'integration_type',
            'Storage Provider Type is required field',
          ),
        },
        {
          field: 'name',
          test: when(
            type => !changeTypeOnly && NAMED.includes(type),
            required('name', 'Storage Provider Name is required field'),
          ),
        },
      ];
    case AddProviderStep.LOCATION:
      return [
        {
          field: 'email',
          test: when(
            notOneStep,
            requiredEmail(
              'email',
              'Email Address is required field',
              'Email Address must be a valid email',
            ),
          ),
        },
        {
          field: 'address_line_one',
          test: when(
            notOneStep,
            required('address_line_one', 'Address 1 is required field'),
          ),
        },
        {
          field: 'country',
          test: when(
            notOneStep,
            required('country', 'Country is required field'),
          ),
        },
        {
          field: 'state',
          test: when(notOneStep, required('state', 'State is required field')),
        },
        {
          field: 'city',
          test: when(notOneStep, required('city', 'City is required field')),
        },
        {
          field: 'zip_code',
          test: when(
            notOneStep,
            required('zip_code', 'Zip Code is required field'),
          ),
        },
        { field: 'username', test: usernameRule },
        {
          field: 'password',
          test: when(
            is(
              IntegrationType.tactical_logistics,
              IntegrationType.mintsoft,
              IntegrationType.santaFe,
              IntegrationType.camelot,
            ),
            required('password', 'Password is required field'),
          ),
        },
        { field: 'host', test: hostRule },
        {
          field: 'depositorCode',
          test: when(
            is(IntegrationType.camelot),
            required('depositorCode', 'Customer Code is required field'),
          ),
        },
        {
          field: 'homeDirectory',
          test: when(
            is(IntegrationType.camelot),
            required('homeDirectory', 'Inventory Directory is required field'),
          ),
        },
        {
          field: 'homeDirectoryShipments',
          test: when(
            is(IntegrationType.camelot),
            required(
              'homeDirectoryShipments',
              'Shipment Directory is required field',
            ),
          ),
        },
      ];
    case AddProviderStep.CONTACT:
      return [
        {
          field: 'contact_email',
          test: requiredEmail(
            'contact_email',
            'Contact Email is required field',
            'Contact Email must be a valid email.',
          ),
        },
        {
          field: 'contact_name',
          test: values => {
            const name = text(values, 'contact_name').trim();
            if (!name) return 'Contact Name is required.';
            return name.length > 60
              ? 'Contact Name must be at most 60 characters'
              : undefined;
          },
        },
        { field: 'contact_phone', test: phoneRule(dialCodeLength) },
      ];
    case AddProviderStep.AUTHENTICATION:
      return [
        {
          field: 'client_id',
          test: required('client_id', 'Client Id is required field'),
        },
        {
          field: 'client_secret',
          test: required('client_secret', 'Client Secret is required field'),
        },
        {
          field: 'clientName',
          test: when(
            is(IntegrationType['3pl']),
            required('clientName', 'User Name is required field'),
          ),
        },
      ];
    default:
      return [];
  }
}

type SchemaOptions = { changeTypeOnly: boolean; dialCodeLength: number };

/** Validates the open step only; the other steps' fields pass as they are. */
export function addProviderSchema(
  step: AddProviderStep,
  options: SchemaOptions,
) {
  const checks = stepChecks(step, options);

  return z.record(z.string(), z.unknown()).superRefine((values, ctx) => {
    for (const { field, test } of checks) {
      const message = test(values as AddProviderValues);
      if (message) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
      }
    }
  });
}

/* Santa Fe's host is stored bare: no scheme, no trailing slash. */
export function cleanHost(url: string): string {
  return url.replace(/^(https?:\/\/)/, '').replace(/\/$/, '');
}

/** What each one-step integration sends to connect, from the shared fields. */
export function credentialsPayload(
  values: AddProviderValues,
): Record<string, unknown> {
  const { quantity_type } = values;

  switch (values.integration_type) {
    case IntegrationType.tactical_logistics:
      return {
        email: values.username,
        password: values.password,
        fulfillment_services: values.fulfillment_services,
        quantity_type,
      };
    case IntegrationType.mintsoft:
      return {
        name: values.username,
        password: values.password,
        quantity_type,
      };
    case IntegrationType.skudrop:
      return { api_key: values.username, quantity_type };
    case IntegrationType.santaFe:
      return {
        host: cleanHost(values.host ?? ''),
        name: values.username,
        password: values.password,
        quantity_type,
      };
    case IntegrationType.camelot:
      return {
        host: values.host,
        name: values.username,
        password: values.password,
        depositorCode: values.depositorCode,
        homeDirectory: values.homeDirectory,
        homeDirectoryShipments: values.homeDirectoryShipments,
        quantity_type,
      };
    case IntegrationType.goodCang:
      return { appToken: values.host, apiKey: values.username, quantity_type };
    case IntegrationType.shipHero:
      /* Changing type sends the token as `code`; creating passes it apart. */
      return { code: values.username, quantity_type };
    default:
      return {};
  }
}

/** Extensiv keys travel nested; the flat fields are left out of the body. */
export function extensivPayload(values: AddProviderValues) {
  const { client_id, client_secret, clientName, ...rest } = values;

  return {
    ...rest,
    api_key: { client_id, client_secret, client_name: clientName },
  };
}
